import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Terminal, X, Minimize, Maximize } from 'lucide-react';

const TerminalEmulator = ({ isMinimized, onToggleMinimize, onClose }) => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([
    { type: 'output', text: 'CYBERBANK TERMINAL v2.0 - Secure Banking System' },
    { type: 'output', text: 'Type "help" for available commands' },
    { type: 'output', text: '' },
  ]);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const terminalRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  useEffect(() => {
    if (!isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isMinimized]);

  const commands = {
    help: () => [
      'Available commands:',
      '  help          - Show this help message',
      '  clear         - Clear terminal',
      '  status        - Show system status',
      '  balance       - Check account balance',
      '  transactions  - List recent transactions',
      '  users         - List system users',
      '  sysinfo       - Display system information',
      '  network       - Network diagnostics',
      '  security      - Security status',
      '  exit          - Close terminal',
    ],
    clear: () => {
      setHistory([]);
      return [];
    },
    status: () => [
      '╔═══════════════════════════════════════╗',
      '║      CYBERBANK SYSTEM STATUS         ║',
      '╠═══════════════════════════════════════╣',
      '║ Database:    [✓] ONLINE              ║',
      '║ API Server:  [✓] OPERATIONAL         ║',
      '║ Security:    [✓] MAXIMUM             ║',
      '║ Network:     [✓] STABLE              ║',
      '║ Encryption:  [✓] AES-256-GCM         ║',
      '╚═══════════════════════════════════════╝',
    ],
    balance: () => [
      'Fetching account balances...',
      '',
      'ACCOUNT SUMMARY:',
      '  ADMIN001: $1,000,000,000.00 USD',
      '',
      'Total Balance: $1,000,000,000.00',
    ],
    transactions: () => [
      'Recent Transactions:',
      '',
      '  1. [COMPLETED] $1,000.00 USD - Terminal Transfer',
      '  2. [PENDING]   $5,000.00 EUR - SWIFT Transfer',
      '  3. [COMPLETED] $2,500.00 USD - S2S Internal',
      '',
      'Use "transaction <id>" for details',
    ],
    users: () => [
      'Active System Users:',
      '',
      '  1. salami      [ADMIN]     Last login: Just now',
      '  2. operator01  [USER]      Last login: 2h ago',
      '',
      'Total users: 2',
    ],
    sysinfo: () => [
      'SYSTEM INFORMATION:',
      '─────────────────────────────────────',
      'OS:           CYBERBANK Linux 6.8.0',
      'Kernel:       6.8.0-cyberbank',
      'Architecture: x86_64',
      'CPU:          Intel Xeon Platinum 8375C @ 2.90GHz',
      'Memory:       32GB RAM',
      'Uptime:       15 days, 7:23:45',
      'Load avg:     0.45, 0.52, 0.48',
      '',
      'Database:     PostgreSQL 16.2',
      'API:          FastAPI 0.115.12',
      'Frontend:     React 19.0.0',
    ],
    network: () => [
      'NETWORK DIAGNOSTICS:',
      '─────────────────────────────────────',
      'Interface:    eth0',
      'IP Address:   172.16.0.100',
      'Subnet Mask:  255.255.255.0',
      'Gateway:      172.16.0.1',
      'DNS Servers:  1.1.1.1, 8.8.8.8',
      '',
      'Active Connections: 127',
      'Bandwidth Usage: 45.2 MB/s',
      'Latency: 12ms',
      '',
      '[✓] VPN Status: ACTIVE (AES-256-GCM)',
      '[✓] Firewall:   ENABLED (2847 rules)',
    ],
    security: () => [
      'SECURITY STATUS:',
      '═════════════════════════════════════',
      '[✓] SELinux:                ENFORCING',
      '[✓] AppArmor:               ACTIVE',
      '[✓] Intrusion Detection:    MONITORING',
      '[✓] SSL/TLS:                ENABLED',
      '[✓] Authentication:         JWT + 2FA',
      '[✓] Encryption:             AES-256-GCM',
      '[✓] Backup:                 AUTOMATED',
      '',
      'Last Security Scan: 15 minutes ago',
      'Threats Detected: 0',
      'Security Level: MAXIMUM',
    ],
    exit: () => {
      onClose();
      return ['Terminal session closed.'];
    },
  };

  const handleCommand = (cmd) => {
    const trimmedCmd = cmd.trim().toLowerCase();
    
    if (!trimmedCmd) return;

    setHistory(prev => [...prev, { type: 'input', text: `$ ${cmd}` }]);
    setCommandHistory(prev => [...prev, cmd]);
    setHistoryIndex(-1);

    if (commands[trimmedCmd]) {
      const output = commands[trimmedCmd]();
      if (output.length > 0) {
        setHistory(prev => [...prev, ...output.map(text => ({ type: 'output', text }))]);
      }
    } else {
      setHistory(prev => [...prev, { type: 'error', text: `Command not found: ${trimmedCmd}` }]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCommand(input);
      setInput('');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex + 1;
        if (newIndex < commandHistory.length) {
          setHistoryIndex(newIndex);
          setInput(commandHistory[commandHistory.length - 1 - newIndex]);
        }
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      } else {
        setHistoryIndex(-1);
        setInput('');
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const partialCmd = input.toLowerCase();
      const matches = Object.keys(commands).filter(cmd => cmd.startsWith(partialCmd));
      if (matches.length === 1) {
        setInput(matches[0]);
      }
    }
  };

  if (isMinimized) {
    return (
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed bottom-4 right-4 z-40"
      >
        <button
          onClick={onToggleMinimize}
          className="flex items-center gap-2 px-4 py-2 terminal-card border border-terminal-green hover:bg-terminal-green/10"
        >
          <Terminal className="w-4 h-4" />
          <span className="text-sm">Terminal</span>
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-4 right-4 w-[800px] h-[500px] z-40"
      style={{ maxWidth: 'calc(100vw - 32px)' }}
    >
      <div className="h-full terminal-card border border-terminal-green flex flex-col">
        {/* Terminal Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-terminal-green/30 bg-terminal-green/5">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4" />
            <span className="text-sm font-bold">CYBERBANK TERMINAL</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleMinimize}
              className="p-1 hover:bg-terminal-green/20 transition-colors"
              title="Minimize"
            >
              <Minimize className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-red-500/20 transition-colors"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Terminal Body */}
        <div
          ref={terminalRef}
          className="flex-1 overflow-y-auto p-4 terminal-font text-sm"
          style={{ scrollbarWidth: 'thin' }}
        >
          {history.map((entry, index) => (
            <div
              key={index}
              className={`${
                entry.type === 'input'
                  ? 'text-terminal-green font-bold'
                  : entry.type === 'error'
                  ? 'text-red-500'
                  : 'text-terminal-green-dim'
              }`}
            >
              {entry.text}
            </div>
          ))}
        </div>

        {/* Terminal Input */}
        <div className="flex items-center gap-2 px-4 py-2 border-t border-terminal-green/30">
          <span className="text-terminal-green">$</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent border-none outline-none text-terminal-green terminal-font"
            placeholder="Enter command..."
            autoFocus
          />
        </div>
      </div>
    </motion.div>
  );
};

export default TerminalEmulator;
